<script setup>
import { mdiCog, mdiAbTesting, mdiChevronUp, mdiChevronDown } from '@mdi/js'
import { computed, ref, onMounted, watch } from 'vue'
import Icon from '@/components/Icon.vue'

const props = defineProps({
  title: {
    type: String,
    default: null
  },
  icon: {
    type: String,
    default: null
  },
  headerIcon: {
    type: String,
    default: null
  },
  // `rounded-xl` is the app's container radius (363 uses against `rounded-lg`'s
  // 197 for controls). This default was the single biggest source of the old
  // mismatch: every one of the 98 cards came out at 1rem while the panels,
  // inputs and buttons around them sat at 0.75rem.
  rounded: {
    type: String,
    default: 'rounded-xl'
  },
  hasTable: Boolean,
  empty: Boolean,
  form: Boolean,
  hoverable: Boolean,
  cardId: {
    type: String,
    default: null
  },
  disableCollapse: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['header-icon-click', 'submit'])

const is = computed(() => props.form ? 'form' : 'div')

const componentClass = computed(() => {
  const base = [
    props.rounded,
    'overflow-hidden'
  ]

  // Hover tints the border and nothing else. The card used to rise on a
  // growing shadow, and a growing shadow still reads as a lift. The border is
  // there at rest, transparent, so the hover only recolours it and never
  // adds the pixel that would nudge the content.
  if (props.hoverable) {
    base.push('border border-transparent hover:border-brand-300 dark:hover:border-brand-700 transition-colors')
  }

  return base
})

const effectiveCardId = computed(() => {
  if (props.cardId) {
    return props.cardId
  }
  if (props.title) {
    return props.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
  }
  return `card-${Math.random().toString(36).substr(2, 9)}`
})

const isCollapsed = ref(false)

onMounted(() => {
  const stored = localStorage.getItem(`card-${effectiveCardId.value}-collapsed`)
  isCollapsed.value = stored !== null ? stored === 'true' : false
})

const shouldShowCollapseIcon = computed(() => {
  return props.title && !props.disableCollapse
})

const computedHeaderIcon = computed(() => {
  if (props.headerIcon !== null && props.headerIcon !== undefined) {
    return props.headerIcon
  }
  return isCollapsed.value ? mdiChevronDown : mdiChevronUp
})

const headerIconClick = () => {
  if (props.headerIcon !== null && props.headerIcon !== undefined) {
    emit('header-icon-click')
    return
  }
  
  isCollapsed.value = !isCollapsed.value
  
  try {
    localStorage.setItem(`card-${effectiveCardId.value}-collapsed`, isCollapsed.value.toString())
  } catch (e) {
    console.warn('Could not save card collapse state to localStorage:', e)
  }
  
  emit('header-icon-click')
}

const submit = e => {
  emit('submit', e)
}

watch(effectiveCardId, (newCardId) => {
  if (newCardId) {
    const stored = localStorage.getItem(`card-${newCardId}-collapsed`)
    isCollapsed.value = stored !== null ? stored === 'true' : false
  }
})
</script>

<template>
  <component
    :is="is"
    :class="componentClass"
    class="bg-white shadow-card dark:bg-gray-800"
    @submit="submit"
  >
    <header
      v-if="title || $slots.header"
      class="flex items-stretch border-b border-gray-100 dark:border-gray-700"
    >
      <!--
        `text-lg` is load-bearing, not decoration. This header carried no size
        class at all, so it inherited the 16px body size and sat two pixels
        above the `text-sm` content beneath it, separated by weight alone.
        Across the 98 cards in 43 views that is most of why the panel reads as
        one flat field of small grey text. 18px puts a real step between a card
        title and its body while staying well under TitleBar's 24/30px h1, so a
        page now has three legible levels instead of two-and-a-bit.
      -->
      <p
        v-if="title"
        class="flex items-center gap-2.5 py-4 px-5 flex-grow text-lg font-semibold text-gray-900 dark:text-gray-100"
      >
        <icon
          v-if="icon"
          :path="icon"
          :size="18"
          w="w-5"
          h="h-5"
          class="flex-shrink-0 text-brand-600 dark:text-brand-400"
        />
        {{ title }}
      </p>
      <div
        v-else
        class="flex items-center py-4 px-5 flex-grow"
      >
        <slot name="header" />
      </div>
      <button
        v-if="shouldShowCollapseIcon"
        type="button"
        class="flex items-center px-4 justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500 dark:hover:text-gray-200 dark:hover:bg-gray-700 transition-colors"
        aria-label="toggle collapse"
        @click.prevent="headerIconClick"
      >
        <icon :path="computedHeaderIcon" :size="20" w="w-5" h="h-5" />
      </button>
    </header>
    <transition name="collapse">
      <div
        v-show="!isCollapsed"
        class="collapse-content"
      >
        <div
          v-if="empty"
          class="text-center py-16 px-6 text-gray-500 dark:text-gray-400"
        >
          <p class="text-sm">Nothing here yet.</p>
        </div>
        <div
          v-else
          :class="{'p-5':!hasTable}"
        >
          <slot />
        </div>
      </div>
    </transition>

    <div
      v-if="isCollapsed && shouldShowCollapseIcon"
      class="border-t border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900"
    >
      <div class="flex items-center justify-center py-2">
        <button
          type="button"
          @click="headerIconClick"
          class="flex items-center gap-1 px-3 py-1 text-xs font-medium text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition-colors rounded-lg"
          aria-label="expand card"
        >
          <icon :path="mdiChevronDown" :size="16" w="w-4" h="h-4" />
          <span>Show content</span>
        </button>
      </div>
    </div>
  </component>
</template>

<style scoped>
.collapse-enter-active,
.collapse-leave-active {
  transition: all 0.3s ease-in-out;
  overflow: hidden;
}

.collapse-enter-from,
.collapse-leave-to {
  opacity: 0;
  max-height: 0;
}

.collapse-enter-to,
.collapse-leave-from {
  opacity: 1;
  max-height: 1000px; /* Adjust this value based on your content */
}

.collapse-content {
  /* Removed will-change property to prevent stacking context issues with modals */
}
</style>
